/* eslint-disable */
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface LeaveReport {
  leaveId: string;
  employeeName: string;
  department: string;
  leaveType: string;
  leaveCategory: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  holidayOverlap: number;
  status: string;
  approvedRejectedBy: string;
  remarks: string;
  leaveBalance: number;
}

export type LeavesListResponse = {
  success: boolean;
  data: LeaveReport[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  message?: string;
};

export type LeavesListFilters = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  leaveType?: string;
  department?: string | number;
  startDate?: string | Date;
  endDate?: string | Date;
};

const formatDateParam = (d?: string | Date) => {
  if (!d) return undefined;
  if (typeof d === "string") return d;

  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
};

const fetchLeavesList = async (
  filters: LeavesListFilters = {}
): Promise<LeavesListResponse> => {
  const {
    page = 1,
    limit = 10,
    search,
    status,
    leaveType,
    department,
    startDate,
    endDate,
  } = filters;

  const params: Record<string, any> = {
    page,
    limit,
  };

  if (search) params.search = search;
  if (status) params.status = status;
  if (leaveType) params.leaveType = leaveType;
  if (department !== undefined && department !== null && department !== "")
    params.department = department;
  const sd = formatDateParam(startDate);
  const ed = formatDateParam(endDate);
  if (sd) params.startDate = sd;
  if (ed) params.endDate = ed;

  const { data } = await customAxios.get<LeavesListResponse>(
    "/reports/leaves/list",
    { params }
  );
  return data;
};

export const useGetLeavesListReport = (
  filters: LeavesListFilters = { page: 1, limit: 10 },
  options?: UseQueryOptions<LeavesListResponse, AxiosError>
): UseQueryResult<LeavesListResponse, AxiosError> =>
  useQuery<LeavesListResponse, AxiosError>(
    ["leaves-list", JSON.stringify(filters)],
    () => fetchLeavesList(filters),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Leave Reports",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load leave reports",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
      ...options,
    }
  );
